import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { camelize } from '@ember/string';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { isNumber } from 'webapp/utils/number';

import eq from 'webapp/helpers/eq';
import didUpdate from 'webapp/modifiers/did-update';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonHtmlContainerComponent from 'webapp/components/common/html-container';
import type { Args as BaseArgs } from 'webapp/components/metric-library/base';
import MetricLibraryBaseComponent from 'webapp/components/metric-library/base';
import MetricLibrarySourceAboutComponent from 'webapp/components/metric-library/source/about';
import MetricLibrarySourceTabsHelpArticlesComponent from 'webapp/components/metric-library/source/tabs/help-articles';
import MetricLibrarySourceTabsMetricsComponent from 'webapp/components/metric-library/source/tabs/metrics';
import MetricLibrarySourceTabsTemplatesComponent from 'webapp/components/metric-library/source/tabs/templates';

import type { SourceRecord } from 'webapp/models';
import type { Metric, Source, SourceType } from 'webapp/models-custom';

import type { ExtraInfoKey } from 'webapp/services/model-manager/source-types';
import type { SourceListEntry } from 'webapp/services/model-manager/sources';

interface Args extends BaseArgs {
	source: SourceRecord | null;
	sourceType: SourceType;
}

const enum TabId {
	METRICS = 'metrics',
	TEMPLATES = 'templates',
	TECH_DETAILS = 'tech_details',
	BEHAVIOUR_AND_USAGE = 'behaviour_and_usage',
	HANDLING_ERRORS = 'handling_errors',
	NOTES = 'notes',
	HELP_ARTICLES = 'help_articles',
}

export const AllSourcesID = 'all';

interface Tab {
	id: TabId;
	title: string;
}

export default class MetricLibrarySourceIndexComponent extends MetricLibraryBaseComponent<Args> {
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('account') declare accountService: Service.Account;

	@tracked selectedTab: TabId = TabId.METRICS;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.mmSourceTypesService.requestExtraInfoByKey(this.args.sourceType.key);

		this.updateSourceTypeMetrics();
	}

	@cached
	get metrics() {
		let metrics: Metric[] = [];
		const source = this.args.source;
		if (source) {
			const sourceId = +source.id!;
			metrics = this.mmMetricsService.metrics.filter(
				(metric) => metric.spaceAccessId === sourceId,
			);
		} else {
			const sourceTypeKey = this.args.sourceType.key;
			metrics = this.mmMetricsService.metrics.filter(
				(metric) =>
					(metric.isTypeHorizon || !metric.isOnSource) &&
					metric.dataSourceTypeKey === sourceTypeKey,
			);
		}

		metrics = metrics.filter((metric: Metric) => !metric.isDeprecated);

		return metrics;
	}

	@cached
	get source(): Source | null {
		let sourceCM = null;

		if (this.args.source) {
			sourceCM = this.mmSourcesService.createModelFromRecord(this.args.source);
		}

		return sourceCM;
	}

	@cached
	get groupedSources() {
		return [
			{
				avatar: 'https://cdn1.databox.com/images/apps/s/Custom.png',
				label: 'All data sources',
				value: AllSourcesID,
			} as SourceListEntry,
		].concat(this.mapSourceListEntries(this.mmSourcesService.sourceListGrouped));
	}

	@cached
	get sourcesOfType() {
		const sourcesOfType = this.mmSourcesService.sourceListGrouped.filter((s) => {
			if (!s.type) {
				// type 0 can be different things (Custom, Wrapper for Zappier, Make and so on)
				if (isNumber(s.value)) {
					// Connected source
					if (s.wrapperFor) {
						return s.wrapperFor === this.args.sourceType.key;
					} else {
						return this.args.sourceType.key === 'Custom';
					}
				} else {
					// Value is string for not connected sources
					return s.value === this.args.sourceType.key;
				}
			}

			return s.type === this.args.sourceType.id;
		});

		return this.mapSourceListEntries(sourcesOfType);
	}

	get templateCount() {
		return this.mmTemplatesService.publicTemplateModels.filter((template) =>
			template.specificSourceTypesKeys.includes(this.args.sourceType.key),
		).length;
	}

	get dynamicTabs() {
		const tabs: Tab[] = this.templateCount
			? [
					{
						id: TabId.TEMPLATES,
						title: TabId.TEMPLATES.toUpperCase(),
					},
				]
			: [];

		const candidates: Tab[] = [
			{
				id: TabId.TECH_DETAILS,
				title: 'TECHNICAL DETAILS',
			},
			{
				id: TabId.BEHAVIOUR_AND_USAGE,
				title: 'BEHAVIORS & USAGE',
			},
			{
				id: TabId.HANDLING_ERRORS,
				title: 'HANDLING ERRORS',
			},
			{
				id: TabId.NOTES,
				title: 'NOTES',
			},
		];

		if (this.args.sourceType.extraInfo) {
			candidates.forEach((tab) => {
				if (this.args.sourceType.extraInfo?.[camelize(tab.id) as ExtraInfoKey]) {
					tabs.push(tab);
				}
			});
		}

		if (
			!this.accountService.hideHelpArticles &&
			this.args.sourceType.uniqueArticleUrls?.length
		) {
			tabs.push({
				id: TabId.HELP_ARTICLES,
				title: 'HELP ARTICLES',
			});
		}

		return tabs;
	}

	get tabs(): Tab[] {
		return [
			{
				id: TabId.METRICS,
				title: TabId.METRICS.toUpperCase(),
			},
			...this.dynamicTabs,
		];
	}

	get hideTabButtons() {
		return this.tabs.length <= 1;
	}

	get isTabMetrics() {
		return this.selectedTab === TabId.METRICS;
	}

	get isTabTemplates() {
		return this.selectedTab === TabId.TEMPLATES;
	}

	get isTabTechDetails() {
		return this.selectedTab === TabId.TECH_DETAILS;
	}

	get isTabBehaviourAndUsage() {
		return this.selectedTab === TabId.BEHAVIOUR_AND_USAGE;
	}

	get isTabHandlingErrors() {
		return this.selectedTab === TabId.HANDLING_ERRORS;
	}

	get isTabNotes() {
		return this.selectedTab === TabId.NOTES;
	}

	get isTabHelpArticles() {
		return this.selectedTab === TabId.HELP_ARTICLES;
	}

	mapSourceListEntries(sourceListEntries: SourceListEntry[]) {
		return sourceListEntries.map((s) => {
			const source = Object.assign({}, s);

			if (!source.isGeneral && !source.label3) {
				source.label3 = `Connected by ${source.createdByName}, ${source.createdOnRelative}`;
			}

			source.hideInDropdown = !!source.isHidden; // show even empty, but not the generic ones marked as hidden
			source.prefixIcon = source.isDataset ? 'ArrowDownRight' : undefined;

			return source;
		});
	}

	@action
	selectTab(tabId: TabId): void {
		this.selectedTab = tabId;
	}

	@action
	onSourceChange(sourceId: string) {
		this.args.onSourceChange(sourceId);
	}

	@action
	updateSourceTypeSetup() {
		this.selectedTab = TabId.METRICS;
		this.mmSourceTypesService.requestExtraInfoByKey(this.args.sourceType.key);
		this.updateSourceTypeMetrics();
	}

	@action
	updateSourceTypeMetrics() {
		if (
			!this.args.source &&
			!this.sourcesOfType.some((s) => s.value === this.args.sourceType.key)
		) {
			// generic source is not on the list, meaning it's connected - select the first source from type
			let firstConnectedSourceOfType;
			if (!this.args.sourceType.id) {
				if (this.args.sourceType.key === 'Custom') {
					firstConnectedSourceOfType = this.mmSourcesService.savedRecords.find(
						(s) => s.type === 0 && !s.wrapperFor,
					);
				} else {
					firstConnectedSourceOfType = this.mmSourcesService.savedRecords.find(
						(item) => item.wrapperFor === this.args.sourceType.key,
					);
				}
			} else {
				firstConnectedSourceOfType = this.mmSourcesService.savedRecords.find(
					(item) => item.type === this.args.sourceType.id,
				);
			}

			this.args.onSourceChange(firstConnectedSourceOfType?.id || '');
		}
	}
	<template>
		<div
			class="metric-library-source bottom-padding"
			{{didUpdate this.updateSourceTypeMetrics this.mmSourcesService.sourceModels.length}}
			{{didUpdate this.updateSourceTypeSetup @sourceType}}
		>
			<MetricLibrarySourceAboutComponent
				@sourceType={{@sourceType}}
				@source={{this.source}}
				@sourceOptions={{this.groupedSources}}
				@sourcesOfType={{this.sourcesOfType}}
				@onSourceChange={{this.onSourceChange}}
			/>

			{{#unless this.hideTabButtons}}
				<div class="metric-library-tab-buttons">
					{{#each this.tabs as |tab|}}
						<a
							class="tab-button
								{{if (eq tab.id this.selectedTab) 'tab-button--selected'}}"
							role="button"
							{{on "click" (fn this.selectTab tab.id)}}
						>
							{{tab.title}}
						</a>
					{{/each}}
				</div>
			{{/unless}}

			{{#if this.isTabMetrics}}
				<MetricLibrarySourceTabsMetricsComponent
					@batchSelect={{this.batchSelect}}
					@sourceType={{@sourceType}}
					@metrics={{this.metrics}}
					@source={{this.source}}
					@onSourceChange={{this.onSourceChange}}
				/>
			{{else if this.isTabTemplates}}
				<MetricLibrarySourceTabsTemplatesComponent @sourceType={{@sourceType}} />
			{{else if this.isTabTechDetails}}
				<CommonHtmlContainerComponent
					@content={{@sourceType.extraInfo.techDetails}}
					@extraClass="metric-library-source-tech_details"
				/>
			{{else if this.isTabBehaviourAndUsage}}
				<CommonHtmlContainerComponent
					@content={{@sourceType.extraInfo.behaviourAndUsage}}
					@extraClass="metric-library-source-behaviour_and_usage"
				/>
			{{else if this.isTabHandlingErrors}}
				<CommonHtmlContainerComponent
					@content={{@sourceType.extraInfo.handlingErrors}}
					@extraClass="metric-library-source-handling_errors"
				/>
			{{else if this.isTabNotes}}
				<CommonHtmlContainerComponent
					@content={{@sourceType.extraInfo.notes}}
					@extraClass="metric-library-source-notes"
				/>
			{{else if this.isTabHelpArticles}}
				<MetricLibrarySourceTabsHelpArticlesComponent
					@sourceType={{@sourceType}}
					@source={{this.source}}
				/>
			{{/if}}

		</div>

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}
	</template>
}
